// ============================================
// CART COMPONENTS - Fixed version
// Fixes: generic 422 error, stale errors, race conditions,
// UI not rolling back, step validation bug, fallbackRemove index bug
// ============================================

function debounce(fn, delay) {
  let timeoutId = null;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function subscribe(event, callback) {
  document.addEventListener(event, callback);
  return () => document.removeEventListener(event, callback);
}

function publish(event, data) {
  document.dispatchEvent(new CustomEvent(event, { detail: data }));
}

function fetchConfig() {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json'
    },
    credentials: 'same-origin'
  };
}

// ---- Quantity limits (change per store; can also be set in theme.liquid:
//      <script>window.CART_LIMITS = { min: 1, max: 5 };</script> ) ----
const CART_LIMITS = Object.assign({ min: 1, max: 5 }, window.CART_LIMITS || {});

function limitMessage(type) {
  if (type === 'min') {
    return `Minimum quantity of ${CART_LIMITS.min} is required for this product.`;
  }
  return `Maximum quantity of ${CART_LIMITS.max} per product is already added to your cart.`;
}

// Turns Shopify / validation errors into a clear customer message
function friendlyCartError(message) {
  const m = String(message || '').toLowerCase();
  if (/minimum|at least/.test(m)) return limitMessage('min');
  if (/only add|can only|all \d+|maximum|\bmax\b|limit|exceed|not available|inventory|stock/.test(m)) {
    return limitMessage('max');
  }
  return message;
}

function trapFocus(container, elementToFocus) {
  if (!container) return;

  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) return;

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  if (elementToFocus) {
    elementToFocus.focus();
  } else if (firstFocusable) {
    firstFocusable.focus();
  }

  container.removeEventListener('keydown', trapFocusHandler);

  function trapFocusHandler(e) {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }

  container.addEventListener('keydown', trapFocusHandler);
}

// ============================================
// CART REMOVE BUTTON
// ============================================
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        const index = this.dataset.index;
        if (index !== undefined) {
          cartItems.updateQuantity(index, 0);
        } else {
          console.error('CartRemoveButton: No index found');
          this.showError('Could not remove item: missing index');
        }
      } else {
        console.error('CartRemoveButton: cart-items component not found');
        this.fallbackRemove();
      }
    });
  }

  showError(message) {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) {
      errors.textContent = message;
    } else {
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
        background: #f44336; color: white; padding: 15px 25px;
        border-radius: 8px; z-index: 9999; font-weight: bold;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;
      errorDiv.textContent = message;
      document.body.appendChild(errorDiv);
      setTimeout(() => errorDiv.remove(), 3000);
    }
  }

  fallbackRemove() {
    const index = parseInt(this.dataset.index);
    if (isNaN(index)) return;

    // data-index is already the 1-based line number (same as updateQuantity uses)
    const body = JSON.stringify({ line: index, quantity: 0 });
    const cartChangeUrl = window.routes?.cart_change_url || '/cart/change';

    fetch(cartChangeUrl, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((data) => {
        if (data.errors) {
          this.showError('Error: ' + data.errors);
        } else {
          location.reload();
        }
      })
      .catch((error) => {
        console.error('Fallback remove error:', error);
        this.showError('Error removing item. Please try again.');
      });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

// ============================================
// CART ITEMS
// ============================================
class CartItems extends HTMLElement {
  // All cart requests run one after another (prevents race conditions)
  requestQueue = Promise.resolve();
  debounceTimers = new Map();
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    if (!this.lineItemStatusElement) {
      this.lineItemStatusElement = document.createElement('div');
      this.lineItemStatusElement.id = 'shopping-cart-line-item-status';
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
      this.lineItemStatusElement.style.display = 'none';
      document.body.appendChild(this.lineItemStatusElement);
    }

    const delay = (typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined') ? ON_CHANGE_DEBOUNCE_TIMER : 300;

    // Separate debounce PER line item (one shared debounce drops changes
    // when two different lines are changed quickly)
    this.addEventListener('change', (event) => {
      const key = event.target?.dataset?.index || 'default';
      clearTimeout(this.debounceTimers.get(key));
      this.debounceTimers.set(
        key,
        setTimeout(() => this.onChange(event), delay)
      );
    });
  }

  connectedCallback() {
    const events = (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : { cartUpdate: 'cart-update' };

    this.cartUpdateUnsubscriber = subscribe(events.cartUpdate, (event) => {
      if (event.detail && event.detail.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  isDrawer() {
    return this.tagName === 'CART-DRAWER-ITEMS' || !!this.closest('cart-drawer');
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) ||
      this.querySelector(`#Drawer-quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value') || '';
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const inputMin = parseInt(event.target.dataset.min);
    const inputMax = parseInt(event.target.max);
    // Use the stricter of the input's own limit and the store limit
    const min = isNaN(inputMin) ? CART_LIMITS.min : Math.max(inputMin, CART_LIMITS.min);
    const max = isNaN(inputMax) ? CART_LIMITS.max : Math.min(inputMax, CART_LIMITS.max);
    const step = parseInt(event.target.step);
    let message = '';

    if (isNaN(inputValue)) {
      message = 'Please enter a valid number';
    } else if (inputValue !== 0 && inputValue < min) {
      message = limitMessage('min');
    } else if (inputValue > max) {
      message = limitMessage('max');
    } else if (!isNaN(step) && step > 0 && inputValue % step !== 0) {
      // FIX: only check step when it is a real number (NaN % x was always "invalid")
      message = (window.quickOrderListStrings?.step_error || 'Quantity must be in multiples of {step}').replace('{step}', step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement?.getAttribute('name') || '',
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    const cartUrl = window.routes?.cart_url || '/cart';

    if (this.isDrawer()) {
      return fetch(`${cartUrl}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => console.error('Error updating cart drawer:', e));
    }

    return fetch(`${cartUrl}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
        }
      })
      .catch((e) => console.error('Error updating cart items:', e));
  }

  getSectionsToRender() {
    const sections = [];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });

    sections.push({
      id: 'cart-live-region-text',
      section: 'cart-live-region-text',
      selector: '.shopify-section',
    });

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  // Public method: queues the request so they never run in parallel
  updateQuantity(line, quantity, name, variantId) {
    this.requestQueue = this.requestQueue
      .then(() => this.performUpdate(line, quantity, name, variantId))
      .catch((e) => console.error('Cart queue error:', e));
    return this.requestQueue;
  }

  async performUpdate(line, quantity, name, variantId) {
    const lineNum = parseInt(line);
    const quantityNum = parseInt(quantity);

    if (isNaN(lineNum) || isNaN(quantityNum)) {
      console.error('Invalid line or quantity:', line, quantity);
      return;
    }

    this.clearErrors(lineNum); // FIX: remove old error text
    this.enableLoading(lineNum);

    let parsedState = null;
    let apiLine = lineNum;
    let liveCart = null;

    try {
      // Line numbers shift when the free gift is added/removed, so find the
      // real line for this variant right before sending the request.
      const resolved = await this.resolveLine(lineNum, variantId);
      liveCart = resolved.cart;
      if (!resolved.found) {
        // Item is already gone from the cart (removed elsewhere): just resync the UI
        await this.onCartUpdate();
        return;
      }
      apiLine = resolved.line;

      const body = JSON.stringify({
        line: apiLine,
        quantity: quantityNum,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const cartChangeUrl = window.routes?.cart_change_url || '/cart/change';
      const response = await fetch(cartChangeUrl, { ...fetchConfig(), body });
      const text = await response.text();

      try {
        parsedState = JSON.parse(text);
      } catch (e) {
        parsedState = null;
      }

      if (!parsedState) {
        throw new Error(`Invalid response from server (status ${response.status})`);
      }

      // FIX: read the REAL Shopify error (422 limit / stock / validation)
      let serverError = parsedState.errors;
      if (!serverError && !response.ok) {
        serverError = parsedState.description || parsedState.message || `Request failed (${response.status})`;
      }

      if (serverError) {
        const msg = typeof serverError === 'string' ? serverError : JSON.stringify(serverError);
        this.handleError(lineNum, friendlyCartError(msg), liveCart?.items?.[apiLine - 1]?.quantity);
        return;
      }
    } catch (error) {
      console.error('Cart update error:', error);
      this.handleError(
        lineNum,
        window.cartStrings?.error || 'There was an error while updating your cart. Please try again.',
        liveCart?.items?.[apiLine - 1]?.quantity
      );
      return;
    } finally {
      this.disableLoading(lineNum);
    }

    // ---- Cart was updated successfully on the server ----
    // UI rendering is in its own try/catch so a rendering bug is NOT shown
    // to the customer as a cart error.
    try {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);

        if (elementToReplace && parsedState.sections?.[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      const quantityElement =
        document.getElementById(`Quantity-${lineNum}`) ||
        document.getElementById(`Drawer-quantity-${lineNum}`);
      const updatedValue = parsedState.items?.[lineNum - 1]?.quantity;
      let message = '';

      if (quantityElement && updatedValue !== undefined && updatedValue !== parseInt(quantityElement.value)) {
        message = (window.cartStrings?.quantityError || 'Quantity updated to {quantity}').replace('{quantity}', updatedValue);
      }
      this.updateLiveRegions(lineNum, message);
      this.handleFocus(lineNum, name, parsedState);
    } catch (renderError) {
      console.error('Cart render error (cart itself was updated):', renderError);
    }

    const events = (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : { cartUpdate: 'cart-update' };
    publish(events.cartUpdate, {
      source: 'cart-items',
      cartData: parsedState,
      variantId: variantId
    });

    // Drawer sections are not in getSectionsToRender(), so refresh the drawer
    if (this.isDrawer()) {
      await this.onCartUpdate();
    }
  }

  // Finds the CURRENT line number of an item using a fresh cart read.
  // Returns { line, cart, found }
  async resolveLine(line, variantId) {
    try {
      if (!variantId) {
        const row =
          document.getElementById(`CartDrawer-Item-${line}`) ||
          document.getElementById(`CartItem-${line}`);
        variantId = row?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId;
      }

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return { line, cart: null, found: true };

      const cart = await response.json();
      if (!variantId) return { line, cart, found: true };

      const same = (item) => String(item.variant_id) === String(variantId);
      // Line is still correct
      if (cart.items[line - 1] && same(cart.items[line - 1])) return { line, cart, found: true };
      // Line moved (e.g. free gift added at the top): find it again
      const idx = cart.items.findIndex((item) => same(item) && item.properties?._gift !== 'true');
      return idx >= 0 ? { line: idx + 1, cart, found: true } : { line, cart, found: false };
    } catch (e) {
      return { line, cart: null, found: true };
    }
  }

  // Show a real message and put the quantity box back to the real cart value
  handleError(line, message, actualQuantity) {
    const quantityElement =
      document.getElementById(`Quantity-${line}`) ||
      document.getElementById(`Drawer-quantity-${line}`);
    if (quantityElement) {
      // Put the box back to what is REALLY in the cart (not a stale value)
      const realQty = actualQuantity !== undefined
        ? String(actualQuantity)
        : (quantityElement.getAttribute('value') || '');
      quantityElement.value = realQty;
      quantityElement.setAttribute('value', realQty);
    }

    this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;

    this.updateLiveRegions(line, message);
  }

  clearErrors(line) {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';

    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = '';
    }
  }

  handleFocus(line, name, parsedState) {
    const lineItem =
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`);
    const cartDrawerWrapper = document.querySelector('cart-drawer');

    if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
      if (cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
      } else {
        lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } else if (parsedState.item_count === 0) {
      if (cartDrawerWrapper) {
        const emptyElement = cartDrawerWrapper.querySelector('.drawer__inner-empty');
        const linkElement = cartDrawerWrapper.querySelector('a');
        if (emptyElement) trapFocus(emptyElement, linkElement);
      }
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message || '';
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      if (message) cartStatus.textContent = message;
      setTimeout(() => cartStatus.setAttribute('aria-hidden', 'true'), 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element ? element.innerHTML : '';
    } catch (e) {
      console.error('Error parsing section HTML:', e);
      return '';
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

// ============================================
// CART NOTE
// ============================================
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        const delay = (typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined') ? ON_CHANGE_DEBOUNCE_TIMER : 300;

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update';

            fetch(cartUpdateUrl, { ...fetchConfig(), body })
              .then((response) => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
              })
              .then((data) => {
                if (data.errors) console.error('Cart note update error:', data.errors);
              })
              .catch((error) => console.error('Error updating cart note:', error));
          }, delay)
        );
      }
    }
  );
}

console.log('✅ Cart components loaded successfully!');